import type { ElementRef } from '@angular/core';
import {
	Component,
	computed,
	inject,
	input,
	signal,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslatePipe } from '@ngx-translate/core';
import { debounceTime } from 'rxjs';

import { cn } from '@valk-nx/core/lib/core';
import { ViewPortService } from '@valk-nx/services/viewport/src/lib/viewport.service';

const lineClampClass = {
	4: 'line-clamp-4',
	6: 'line-clamp-6',
} as const;

type Lines = keyof typeof lineClampClass;

@Component({
	encapsulation: ViewEncapsulation.None,
	imports: [TranslatePipe],
	selector: 'vp-read-more',
	templateUrl: './read-more.html',
})
export class ReadMoreComponent {
	private readonly viewPortService = inject(ViewPortService);

	lines = input<Lines>(4);

	expanded = signal(false);
	screenResized = signal(0);

	clampClass = computed(() => lineClampClass[this.lines()]);

	// only when clamped is known for sure to be false and not expanded, hide toggle
	// clamped can only be determined when not expanded
	showToggle = computed(() => {
		if (this.expanded()) {
			return true;
		}
		// depend on screenResized just to be notified that we might need to check the overflow again
		this.screenResized();
		// check overflow after being collapsed even without a resize
		return this.overflow() > 0;
	});

	@ViewChild('content') content!: ElementRef<HTMLDivElement>;

	protected readonly cn = cn;

	constructor() {
		this.viewPortService.screenWidth$
			.pipe(takeUntilDestroyed(), debounceTime(300))
			.subscribe(() => this.updateScreenResized());
	}

	updateScreenResized() {
		this.screenResized.set(this.overflow());
	}

	overflow() {
		const el = this.content?.nativeElement;
		if (el && !this.expanded()) {
			return el.scrollHeight - el.clientHeight;
		}
		return -1;
	}

	toggle() {
		this.expanded.set(!this.expanded());
	}
}
